import React, { useEffect, useMemo, useState } from 'react';
import { Select, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AddressInput } from '../../../../../common/Form/AddressInput/AddressInput';
import { PhoneNumberInput } from '../../../../../common/Form/PhoneNumberInput/PhoneNumberInput';
import TextField from '../../../../../common/Form/TextField/TextField';
import { maxMobileSize, minSymbolQTY, phoneTypes, validationMessages } from '../../../../../../utils/constants';
import { usePrevious, useWindowSize } from '../../../../../../utils/hooks';
import BaseModal from '../../../../../common/ModalWindows/BaseModal';
import ValidationErrorLabel from '../../../../../common/ValidationErrorLabel/ValidationErrorLabel';
import { emailRegExp } from '../../../../../../utils/regexps';
import api from '../../../../../../api/apiSettings';
import colors from '../../../../../../scss/colors';

function EditPersonalDetails(props) {
  const { data, state, changeState, updateUserData, updating } = props;

  const prevUpdating = usePrevious(updating);
  const size = useWindowSize();
  const { t } = useTranslation();
  const validationMessage = useMemo(() => validationMessages(t), [t]);

  const {
    FirstName,
    LastName,
    AddressLine1,
    AddressLine2,
    SecondaryEmail,
    Phone,
    PhoneType,
    PhoneCountryCode,
    Country,
    State,
    City
  } = data || {};

  const [newData, setNewData] = useState({
    FirstName,
    LastName,
    SecondaryEmail,
    Phone,
    PhoneCountryCode,
    PhoneType,
    AddressLine1,
    AddressLine2,
    Country,
    State,
    City
  });

  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    phone: false,
    country: false,
    city: false,
    state: false,
    email: false,
    customError: false
  });

  const [phone, setPhone] = useState(`${newData.PhoneCountryCode}${newData.Phone}`);

  const validateForm = () => {
    setErrors({
      firstName: !newData.FirstName,
      lastName: !newData.LastName,
      phone: !newData.Phone,
      country: !newData.Country,
      city: !newData.City,
      state: newData.Country === 'United States' ? !newData.State : false,
      email: !!newData.SecondaryEmail?.length && !newData.SecondaryEmail.match(emailRegExp)?.length
    });
  };

  const clickSave = async () => {
    if (Object.values(errors).some((value) => value)) return;

    // const countryFromApi = await api.get(`v1/get-countries?keyword=${newData.Country}`);
    // if (newData.Country !== countryFromApi.data?.Data?.[0]?.Name) {
    //   setErrors((prev) => ({
    //     ...prev,
    //     customError: validationMessage?.VALIDATION_LOCATION_COUNTRY
    //   }));
    //   return;
    // }

    // if (newData.State) {
    //   const stateFromApi = await api.get(
    //     `v1/get-states-by-country?countryName=${newData.Country}&keyword=${newData.State}`
    //   );
    //   if (newData.State !== stateFromApi.data?.Data?.[0]?.Name) {
    //     setErrors((prev) => ({
    //       ...prev,
    //       customError: validationMessage?.VALIDATION_LOCATION_STATE
    //     }));
    //     return;
    //   }
    // }

    // const cityFromApi = await api.get(
    //   `v1/get-cities-by-state?countryName=${newData.Country}&stataName=${newData.State}&keyword=${newData.City}`
    // );
    // if (newData.City !== cityFromApi.data?.Data?.[0]?.Name) {
    //   setErrors((prev) => ({
    //     ...prev,
    //     customError: validationMessage.VALIDATION_LOCATION_CITY
    //   }));
    //   return;
    // }

    await updateUserData({ ...newData });
  };

  useEffect(() => {
    validateForm();
  }, [newData.FirstName, newData.LastName, newData.Phone, newData.Country, newData.City, newData.SecondaryEmail]);

  useEffect(() => {
    if (prevUpdating === true && updating === false) changeState(false);
  }, [updating]);

  return (
    <BaseModal
      open={state}
      fullscreen={size.width < maxMobileSize}
      closeHandler={() => {
        setErrors({ ...errors, customError: false });
        changeState(false);
      }}
      confirmHandler={clickSave}
      title="Edit Personal Details"
      pending={updating}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
        <TextField
          placeholder={t('common.forms.firstname')}
          label={t('common.forms.firstname')}
          labelClass="text-sm"
          inputClass="!text-sm p-3 text-black !font-normal !rounded-lg"
          value={newData.FirstName}
          onChange={(e) => setNewData((x) => ({ ...x, FirstName: e.target.value }))}
          error={errors.firstName}
          errorText={errors.firstName && validationMessage.INFO_REQ}
        />
        <TextField
          placeholder={t('common.forms.lastname')}
          label={t('common.forms.lastname')}
          labelClass="text-sm"
          inputClass="!text-sm p-3 text-black !font-normal !rounded-lg"
          value={newData.LastName}
          onChange={(e) => setNewData((x) => ({ ...x, LastName: e.target.value }))}
          error={errors.lastName}
          errorText={errors.lastName && validationMessage.INFO_REQ}
        />
        <TextField
          placeholder={t('common.forms.secondaryplaceholder')}
          label={t('common.forms.secondaryplaceholder')}
          labelClass="text-sm"
          inputClass="!text-sm p-3 text-black !font-normal !rounded-lg"
          value={newData.SecondaryEmail}
          onChange={(e) => setNewData((x) => ({ ...x, SecondaryEmail: e.target.value }))}
          error={errors.email}
          errorText={errors.email && validationMessage.EMAIL_INVALID}
        />
        <div className="flex flex-col gap-2">
          <PhoneNumberInput
            phone={phone}
            onChange={(newPhone, code) => {
              if (code.dialCode === '7' && code.countryCode === 'kz') {
                setPhone(newPhone.replace(/7/, "7310"));
              } else {
                setPhone(newPhone);
              }

              setNewData((x) => ({
                ...x,
                Phone: `${newPhone}`.replace(new RegExp(`${code.dialCode}`), ''),
                PhoneCountryCode: `${code.dialCode ? '+' : ''}${code.dialCode}`
              }));
            }}
            error={errors.phone && validationMessage.PHONE_NUMBER_REQ}
          />
          {newData.Phone?.length !== 0 && newData.Phone?.length <= minSymbolQTY.PHONE && (
            <ValidationErrorLabel message={validationMessage.PHONE_NUMBER_INVALID} />
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm text-black">{t('common.forms.phonetype')}</label>
          <Select
            value={newData.PhoneType || phoneTypes[0].value}
            onChange={(e) => setNewData((x) => ({ ...x, PhoneType: e.target.value }))}
            sx={{
              '& legend': { display: 'none' },
              '& fieldset': { top: 0, fontSize: '14px' },
              '& .MuiOutlinedInput-notchedOutline': {
                borderRadius: '0.5rem',
                border: `1px solid ${colors.LIGHT_GREY}`
              },
              '& .MuiInputBase-root': {
                fontSize: '14px',
                padding: '10px'
              },
              '& .MuiSelect-select': {
                fontSize: '14px',
                padding: '11px 15px'
              }
            }}
            MenuProps={{ style: { zIndex: 1610, fontSize: '14px' } }}
          >
            {phoneTypes.map((type) => (
              <MenuItem sx={{ fontSize: '14px' }} key={type.id} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>

      <AddressInput
        userData={data}
        updateUserData={setNewData}
        edit
        errors={
          (errors.country || errors.city || errors.state) && {
            country: errors.country && validationMessage.COUNTRY_REQ,
            city: errors.city && validationMessage.CITY_REQ,
            state: errors.state && validationMessage.STATE_REQ
          }
        }
      />
      {errors.customError && <ValidationErrorLabel message={errors.customError} />}
    </BaseModal>
  );
}

export default EditPersonalDetails;
